import API from '../api/axios'; // The configured axios instance
import { invalidateCache, clearAllCache, fetchWithCache } from '../utils/apiCache';

// =================================================================
// Notification API Calls
// =================================================================

export const getNotifications = async (page = 1, limit = 10) => {
    const cacheKey = `notifications_page_${page}_limit_${limit}`;
    return fetchWithCache(cacheKey, async () => {
        const { data } = await API.get(`/api/users/notifications?page=${page}&limit=${limit}`);
        return data;
    });
};

export const getUnreadNotificationsCount = async () => {
    // Very short TTL for the unread count to keep it reasonably fresh but still cached
    return fetchWithCache('notifications_unread_count', async () => {
        const { data } = await API.get('/api/users/notifications?page=1&limit=1');
        return data.unreadCount || 0;
    }, 30 * 1000);
};

export const markNotificationAsRead = async (id) => {
    const { data } = await API.patch(`/api/users/notifications/${id}/read`);
    invalidateCache([/notifications_page_.*/, 'notifications_unread_count']);
    return data;
};

export const markAllNotificationsAsRead = async () => {
    const { data } = await API.patch('/api/users/notifications/read-all');
    invalidateCache([/notifications_page_.*/, 'notifications_unread_count']);
    return data;
};

export const sendAdminNotification = async (payload) => {
    const { data } = await API.post('/api/admin/monitoring/notifications/send', payload);
    invalidateCache(/admin_notifications_history_.*/);
    return data;
};

export const getAdminNotificationHistory = async (page = 1, limit = 20) => {
    const cacheKey = `admin_notifications_history_page_${page}`;
    return fetchWithCache(cacheKey, async () => {
        const { data } = await API.get(`/api/admin/monitoring/notifications/history?page=${page}&limit=${limit}`);
        return data;
    });
};

export const getAdminFarmers = async () => {
    return fetchWithCache('admin_farmers_list', async () => {
        const { data } = await API.get('/api/users/admin/farmers');
        return data;
    }, 5 * 60 * 1000);
};

// =================================================================
// Cache Invalidation Helpers
// =================================================================

/**
 * Invalidates analytics-related caches for a specific field.
 * Should be called whenever data that affects health score or savings is updated.
 */
export const invalidateFieldAnalytics = (fieldId) => {
    if (!fieldId) return;
    invalidateCache([
        `health_score_field_${fieldId}`,
        `savings_field_${fieldId}`,
        `field_health_${fieldId}`,
        `nutrient_balance_field_${fieldId}`,
        new RegExp(`recommendations_field_${fieldId}_page_.*`),
        new RegExp('recommendations_all_page_.*')
    ]);
};

// =================================================================
// User API Calls
// =================================================================

export const loginUser = async (credentials) => {
    const { data } = await API.post('/api/users/login', credentials);
    clearAllCache(); // Clear everything on login to ensure fresh state
    return data;
};

export const registerUser = async (userData) => {
    const { data } = await API.post('/api/users/register', userData);
    clearAllCache(); // Clear everything on registration
    return data;
};

export const googleLogin = async (idToken) => {
    const { data } = await API.post('/api/users/google-login', { idToken });
    if (!data.isNewUser) {
        clearAllCache();
    }
    return data;
};

export const googleRegister = async (userData) => {
    const { data } = await API.post('/api/users/google-register', userData);
    clearAllCache();
    return data;
};

export const changePassword = async (passwordData) => {
    const { data } = await API.put('/api/users/change-password', passwordData);
    return data;
};

export const requestPasswordReset = async (identifier) => {
    const { data } = await API.post('/api/password-reset/request', { identifier });
    return data;
};

// =================================================================
// Profile & User Management
// =================================================================

export const getUserProfile = async () => {
    return fetchWithCache('user_profile', async () => {
        const { data } = await API.get('/api/users/profile');
        return data;
    }, 10 * 60 * 1000);
};

export const updateUserProfile = async (profileData) => {
    const { data } = await API.put('/api/users/profile', profileData);
    invalidateCache('user_profile');
    return data;
};

export const updatePreferredLanguage = async (language) => {
    const { data } = await API.put('/api/users/preferred-language', { language });
    invalidateCache('user_profile');
    return data;
};

export const getFarmers = async () => {
    return fetchWithCache('admin_farmers_all', async () => {
        const { data } = await API.get('/api/users/admin/farmers');
        return data;
    }, 10 * 60 * 1000);
};

// =================================================================
// Field API Calls
// =================================================================

export const getFieldsForUser = async (userId) => {
    return fetchWithCache(`fields_user_${userId}`, async () => {
        const { data } = await API.get(`/api/fields/admin/user/${userId}`);
        return data;
    });
};

export const getFields = async () => {
    return fetchWithCache('fields_all', async () => {
        const { data } = await API.get('/api/fields');
        return data;
    });
};

export const getAppConfig = async () => {
    return fetchWithCache('app_config', async () => {
        const { data } = await API.get('/api/master/config');
        return data;
    }, 60 * 60 * 1000);
};

export const getFieldById = async (id) => {
    return fetchWithCache(`field_${id}`, async () => {
        const { data } = await API.get(`/api/fields/${id}`);
        return data;
    });
};

export const addField = async (fieldData) => {
  if (!fieldData?.name || !fieldData?.area) {
    throw new Error("Field creation attempted with incomplete data.");
  }
  const { data } = await API.post('/api/fields', fieldData);
  invalidateCache(/fields_all|fields_user_.*/); // Clear field lists
  return data;
};

export const updateFieldStatus = async (id, status) => {
  const { data } = await API.patch(`/api/fields/${id}/status`, { isActive: status });
  invalidateCache([`field_${id}`, 'fields_all', /fields_user_.*/]);
  invalidateFieldAnalytics(id);
  return data;
};

export const deleteField = async (id) => {
    const { data } = await API.delete(`/api/fields/${id}`);
    invalidateCache([`field_${id}`, 'fields_all', /fields_user_.*/]);
    invalidateFieldAnalytics(id);
    return data;
};

export const updateField = async (id, fieldData) => {
  const { data } = await API.patch(`/api/fields/${id}`, fieldData);
  invalidateCache([`field_${id}`, 'fields_all', /fields_user_.*/]);
  invalidateFieldAnalytics(id);
  return data;
};

export const getFieldHealthHistory = async (id) => {
    return fetchWithCache(`field_health_${id}`, async () => {
        const { data } = await API.get(`/api/fields/${id}/health`);
        return data;
    });
};

export const refreshFieldHealth = async (id) => {
  const { data } = await API.post(`/api/fields/${id}/refresh-health`);
  invalidateFieldAnalytics(id);
  return data;
};

// =================================================================
// Crop API Calls
// =================================================================

export const getCropsForField = async (fieldId) => {
    return fetchWithCache(`crops_field_${fieldId}`, async () => {
        const { data } = await API.get(`/api/crops/${fieldId}`);
        return data;
    });
};

export const getGrowthStagesByCrop = async (cropId) => {
    return fetchWithCache(`growth_stages_crop_${cropId}`, async () => {
        const { data } = await API.get(`/api/crops/stages/${cropId}`);
        return data;
    }, 60 * 60 * 1000); // 1 hour cache
};

export const getCropById = async (cropId) => {
    return fetchWithCache(`crop_${cropId}`, async () => {
        const { data } = await API.get(`/api/crops/details/${cropId}`);
        return data;
    });
};

export const addCrop = async (cropData) => {
    const { data } = await API.post('/api/crops', cropData);
    invalidateCache(`crops_field_${cropData.fieldId}`);
    invalidateFieldAnalytics(cropData.fieldId);
    return data;
};

export const addCropImage = async (cropId, formData) => {
    const { data } = await API.patch(`/api/crops/${cropId}/images`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
    invalidateCache(`crop_${cropId}`);
    return data;
};

export const updateCropStage = async (id, stage) => {
    const { data } = await API.patch(`/api/crops/${id}/stage`, { currentStage: stage });
    invalidateCache([`crop_${id}`, /crops_field_.*/]);
    return data;
};

export const deleteCropImage = async (cropId, telegramFileId) => {
  const { data } = await API.delete(`/api/crops/${cropId}/images`, {
    data: { telegramFileId },
  });
  invalidateCache(`crop_${cropId}`);
  return data;
};

// =================================================================
// Soil Report API Calls
// =================================================================

export const uploadSoilReport = async (fieldId, formData) => {
    const { data } = await API.post(`/api/soil-reports/upload/${fieldId}`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
    invalidateCache(`soil_reports_field_${fieldId}`);
    return data;
};

export const getSoilReportsByField = async (fieldId) => {
    return fetchWithCache(`soil_reports_field_${fieldId}`, async () => {
        const { data } = await API.get(`/api/soil-reports/by-field/${fieldId}`);
        return data;
    });
};

export const deleteSoilReport = async (reportId) => {
    const { data } = await API.delete(`/api/soil-reports/report/${reportId}`);
    invalidateCache(/soil_reports_field_.*/);
    return data;
};

// =================================================================
// Fertilizer Master API Calls (for farmer selection)
// =================================================================

export const getFertilizerMasters = async () => {
    return fetchWithCache('master_fertilizers', async () => {
        const { data } = await API.get('/api/master/fertilizers');
        return data;
    }, 60 * 60 * 1000);
};


// =================================================================
// Fertilizer Application API Calls
// =================================================================

export const recordFertilizerApplication = async (applicationData) => {
    const { data } = await API.post('/api/fertilizer-applications', applicationData);
    invalidateCache([
        `fertilizer_apps_field_${applicationData.fieldId}`,
        `fertilizer_apps_crop_${applicationData.cropId}`
    ]);
    invalidateFieldAnalytics(applicationData.fieldId);
    return data;
};

export const getFertilizerApplicationsByField = async (fieldId) => {
    return fetchWithCache(`fertilizer_apps_field_${fieldId}`, async () => {
        const { data } = await API.get(`/api/fertilizer-applications/field/${fieldId}`);
        return data;
    });
};

export const getFertilizerApplicationsByCrop = async (cropId) => {
    return fetchWithCache(`fertilizer_apps_crop_${cropId}`, async () => {
        const { data } = await API.get(`/api/fertilizer-applications/crop/${cropId}`);
        return data;
    });
};

// =================================================================
// Pesticide Master API Calls
// =================================================================

export const getPesticideMasters = async () => {
    return fetchWithCache('master_pesticides', async () => {
        const { data } = await API.get('/api/master/pesticides');
        return data;
    }, 60 * 60 * 1000);
};

// =================================================================
// Pesticide Application API Calls
// =================================================================

export const recordPesticideApplication = async (applicationData) => {
    const { data } = await API.post('/api/pesticide-applications', applicationData);
    invalidateCache([
        `pesticide_apps_field_${applicationData.fieldId}`,
        `pesticide_apps_crop_${applicationData.cropId}`
    ]);
    invalidateFieldAnalytics(applicationData.fieldId);
    return data;
};

export const getPesticideApplicationsByField = async (fieldId) => {
    return fetchWithCache(`pesticide_apps_field_${fieldId}`, async () => {
        const { data } = await API.get(`/api/pesticide-applications/field/${fieldId}`);
        return data;
    });
};

export const getPesticideApplicationsByCrop = async (cropId) => {
    return fetchWithCache(`pesticide_apps_crop_${cropId}`, async () => {
        const { data } = await API.get(`/api/pesticide-applications/crop/${cropId}`);
        return data;
    });
};

// =================================================================
// Insecticide Master API Calls
// =================================================================

export const getInsecticideMasters = async () => {
    return fetchWithCache('master_insecticides', async () => {
        const { data } = await API.get('/api/master/insecticides');
        return data;
    }, 60 * 60 * 1000);
};

// =================================================================
// Insecticide Application API Calls
// =================================================================

export const recordInsecticideApplication = async (applicationData) => {
    const { data } = await API.post('/api/insecticide-applications', applicationData);
    invalidateCache([
        `insecticide_apps_field_${applicationData.fieldId}`,
        `insecticide_apps_crop_${applicationData.cropId}`
    ]);
    invalidateFieldAnalytics(applicationData.fieldId);
    return data;
};

export const getInsecticideApplicationsByField = async (fieldId) => {
    return fetchWithCache(`insecticide_apps_field_${fieldId}`, async () => {
        const { data } = await API.get(`/api/insecticide-applications/field/${fieldId}`);
        return data;
    });
};

export const getInsecticideApplicationsByCrop = async (cropId) => {
    return fetchWithCache(`insecticide_apps_crop_${cropId}`, async () => {
        const { data } = await API.get(`/api/insecticide-applications/crop/${cropId}`);
        return data;
    });
};

// =================================================================
// Recommendation API Calls
// =================================================================

export const generateRecommendationForField = async (fieldId) => {
    const { data } = await API.post(`/api/recommendations/field/${fieldId}`);
    invalidateCache([`recommendations_field_${fieldId}`, 'recommendations_all']);
    invalidateFieldAnalytics(fieldId);
    return data;
};

export const getRecommendationsByField = async (fieldId, page = 1, limit = 3) => {
    return fetchWithCache(`recommendations_field_${fieldId}_page_${page}_limit_${limit}`, async () => {
        const { data } = await API.get(`/api/recommendations/field/${fieldId}?page=${page}&limit=${limit}`);
        return data;
    });
};

export const getMyRecommendations = async (page = 1, limit = 3) => {
    return fetchWithCache(`recommendations_all_page_${page}_limit_${limit}`, async () => {
        const { data } = await API.get(`/api/recommendations?page=${page}&limit=${limit}`);
        return data;
    });
};

export const getWeatherForField = async (fieldId) => {
    return fetchWithCache(`weather_field_${fieldId}`, async () => {
        const { data } = await API.get(`/api/recommendations/field/${fieldId}/weather`);
        return data;
    }, 30 * 60 * 1000);
};

export const applyRecommendation = async (id, fieldId) => {
    const { data } = await API.post(`/api/recommendations/${id}/apply`);
    invalidateCache([`recommendation_${id}`, `recommendations_field_${fieldId}`, `recommendations_all_page_.*`]);
    invalidateFieldAnalytics(fieldId);
    // Also invalidate fertilizer logs as they are auto-created
    invalidateCache([`fertilizer_apps_field_${fieldId}`, `timeline_field_${fieldId}_.*`]);
    return data;
};

export const undoApplyRecommendation = async (id, fieldId) => {
    const { data } = await API.post(`/api/recommendations/${id}/undo-apply`);
    invalidateCache([`recommendation_${id}`, `recommendations_field_${fieldId}`, `recommendations_all_page_.*`]);
    invalidateFieldAnalytics(fieldId);
    invalidateCache([`fertilizer_apps_field_${fieldId}`, `timeline_field_${fieldId}_.*`]);
    return data;
};

// =================================================================
// Nutrient Balance Engine API Calls
// =================================================================

export const getNutrientBalance = async (fieldId) => {
    return fetchWithCache(`nutrient_balance_field_${fieldId}`, async () => {
        const { data } = await API.get(`/api/analytics/nutrient-balance/${fieldId}`);
        return data;
    });
};

// =================================================================
// Phase 6 — Irrigation Log API Calls
// =================================================================

export const addIrrigationLog = async (fieldId, logData) => {
    const { data } = await API.post(`/api/fields/${fieldId}/irrigation`, logData);
    invalidateCache(`irrigation_logs_field_${fieldId}`);
    invalidateFieldAnalytics(fieldId);
    return data;
};

export const getIrrigationLogs = async (fieldId) => {
    return fetchWithCache(`irrigation_logs_field_${fieldId}`, async () => {
        const { data } = await API.get(`/api/fields/${fieldId}/irrigation`);
        return data;
    });
};

export const updateIrrigationLog = async (logId, logData) => {
    const { data } = await API.patch(`/api/irrigation/${logId}`, logData);
    invalidateCache(/irrigation_logs_field_.*/);
    // Invalidate analytics for all fields as we don't have the fieldId here
    invalidateCache([/health_score_field_.*/, /savings_field_.*/, /field_health_.*/]);
    return data;
};

export const deleteIrrigationLog = async (logId) => {
    const { data } = await API.delete(`/api/irrigation/${logId}`);
    invalidateCache(/irrigation_logs_field_.*/);
    invalidateCache([/health_score_field_.*/, /savings_field_.*/, /field_health_.*/]);
    return data;
};

// =================================================================
// Phase 6 — Field Issue API Calls
// =================================================================

export const addFieldIssue = async (fieldId, formData) => {
    const { data } = await API.post(`/api/fields/${fieldId}/issues`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    invalidateCache(`field_issues_field_${fieldId}`);
    invalidateFieldAnalytics(fieldId);
    return data;
};

export const getFieldIssues = async (fieldId) => {
    return fetchWithCache(`field_issues_field_${fieldId}`, async () => {
        const { data } = await API.get(`/api/fields/${fieldId}/issues`);
        return data;
    });
};

export const updateFieldIssue = async (issueId, issueData) => {
    const { data } = await API.patch(`/api/issues/${issueId}`, issueData);
    invalidateCache(/field_issues_field_.*/);
    invalidateCache([/health_score_field_.*/, /savings_field_.*/, /field_health_.*/]);
    return data;
};

// =================================================================
// Phase 6 — Field Image API Calls
// =================================================================

export const addFieldImage = async (fieldId, formData) => {
    const { data } = await API.post(`/api/fields/${fieldId}/images`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    invalidateCache(`field_images_field_${fieldId}`);
    invalidateFieldAnalytics(fieldId);
    return data;
};

export const getFieldImages = async (fieldId) => {
    return fetchWithCache(`field_images_field_${fieldId}`, async () => {
        const { data } = await API.get(`/api/fields/${fieldId}/images`);
        return data;
    });
};

export const deleteFieldImage = async (imageId) => {
    const { data } = await API.delete(`/api/field-images/${imageId}`);
    invalidateCache(/field_images_field_.*/);
    invalidateCache([/health_score_field_.*/, /savings_field_.*/, /field_health_.*/]);
    return data;
};

// =================================================================
// Admin Image Review API Calls
// =================================================================

export const getPendingReviewImages = async (page = 1, limit = 10, status = 'pending_review') => {
    const { data } = await API.get(`/api/admin/images/pending-review?page=${page}&limit=${limit}&status=${status}`);
    return data;
};

export const updateImageAnalysis = async (imageId, updateData) => {
    const { data } = await API.put(`/api/admin/images/${imageId}/update-analysis`, updateData);
    invalidateCache([/field_images_field_.*/]);
    return data;
};

export const retryAiAnalysis = async (imageId) => {
    const { data } = await API.post(`/api/admin/images/${imageId}/retry-analysis`);
    invalidateCache([/field_images_field_.*/]);
    return data;
};

// =================================================================
// Phase 6 — Growth Stage History API Calls
// =================================================================

export const addStageHistory = async (fieldId, stageData) => {
    const { data } = await API.post(`/api/fields/${fieldId}/stage-history`, stageData);
    invalidateCache(`stage_history_field_${fieldId}`);
    invalidateFieldAnalytics(fieldId);
    return data;
};

export const getStageHistory = async (fieldId, cropId) => {
    const cacheKey = `stage_history_field_${fieldId}_${cropId || 'all'}`;
    return fetchWithCache(cacheKey, async () => {
        const params = cropId ? `?cropId=${cropId}` : '';
        const { data } = await API.get(`/api/fields/${fieldId}/stage-history${params}`);
        return data;
    });
};

// =================================================================
// Phase 6 — Activity Timeline API Calls
// =================================================================

export const getActivityTimeline = async (fieldId, options = {}) => {
    const cacheKey = `timeline_field_${fieldId}_${options.cursor || 'start'}`;
    return fetchWithCache(cacheKey, async () => {
        const params = new URLSearchParams();
        if (options.cursor) params.append('cursor', options.cursor);
        if (options.limit) params.append('limit', options.limit);
        if (options.startDate) params.append('startDate', options.startDate);
        if (options.endDate) params.append('endDate', options.endDate);
        const queryString = params.toString() ? `?${params.toString()}` : '';
        const { data } = await API.get(`/api/fields/${fieldId}/timeline${queryString}`);
        return data;
    }, 2 * 60 * 1000);
};

// =================================================================
// Unified Agronomic Analysis (Gemini + Sarvam)
// =================================================================

export const getUnifiedAgronomicAnalysis = async (fieldId, formData) => {
    const { data } = await API.post(`/api/agronomic-analysis/${fieldId}`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
    invalidateCache([`field_health_${fieldId}`, `recommendations_field_${fieldId}`]);
    invalidateFieldAnalytics(fieldId);
    return data;
};

export const getFieldTimelineIntelligence = async (fieldId) => {
    return fetchWithCache(`timeline_intelligence_field_${fieldId}`, async () => {
        const { data } = await API.get(`/api/fields/${fieldId}/timeline-intelligence`);
        return data;
    }, 5 * 60 * 1000); // 5 minute cache
};

export const getAgronomicAnalysisHistory = async (fieldId) => {
    const { data } = await API.get(`/api/agronomic-analysis/field/${fieldId}`);
    return data;
};

export const getAgronomicAnalysisById = async (id) => {
    const { data } = await API.get(`/api/agronomic-analysis/${id}`);
    return data;
};

export const getAgronomicAnalysisQuota = async () => {
    const { data } = await API.get('/api/agronomic-analysis/quota');
    return data;
};
